<template>
  <div class="na-page na-staff">
    <StaffHeader role="Staff" />

    <main class="st-wrap st-below">
      <div class="st-head">
        <div>
          <span class="st-kicker">Your earnings</span>
          <h1 class="st-title">Payments</h1>
          <p class="st-sub">Every payout you've earned from towns and papers you've brought on. Filter by date and export a statement for your records.</p>
        </div>
        <button type="button" class="st-btn outline" @click="goBack"><v-icon size="18">mdi-arrow-left</v-icon>Back to dashboard</button>
      </div>

      <!-- KPIs -->
      <div class="kpis">
        <div class="st-card kpi">
          <span class="k-label">Total paid</span>
          <span class="k-value">{{ money(totalPaid) }}</span>
          <span class="k-meta">{{ paidCount }} {{ paidCount === 1 ? 'payment' : 'payments' }}</span>
        </div>
        <div class="st-card kpi">
          <span class="k-label">Paid this year</span>
          <span class="k-value">{{ money(paidThisYear) }}</span>
          <span class="k-meta">Since January 1, {{ thisYear }}</span>
        </div>
        <div class="st-card kpi" :class="{ owed: outstandingCount }">
          <span class="k-label">Outstanding</span>
          <span class="k-value">{{ money(totalOutstanding) }}</span>
          <span class="k-meta">{{ outstandingCount }} not yet paid</span>
        </div>
        <div class="st-card kpi">
          <span class="k-label">Last payment</span>
          <span class="k-value small">{{ lastPaymentDate ? formatDay(lastPaymentDate) : '—' }}</span>
          <svg v-if="payments.length > 1" class="spark" viewBox="0 0 200 40" preserveAspectRatio="none" aria-hidden="true">
            <polyline :points="sparklinePoints" fill="none" stroke="#B8925A" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
          </svg>
          <span class="k-meta">{{ payments.length > 1 ? `Trend of your last ${lastAmounts.length} amounts` : 'Trend appears after two payments' }}</span>
        </div>
      </div>

      <!-- History -->
      <section class="st-card">
        <div class="st-card-head">
          <h2 class="st-card-title">Payment history <span class="st-count">{{ filteredPayments.length }}</span></h2>
          <button type="button" class="st-btn primary sm" :disabled="!filteredPayments.length" @click="exportCSV"><v-icon size="18">mdi-download</v-icon>Export CSV</button>
        </div>

        <div class="filters">
          <div class="st-field">
            <label for="pay-status">Status</label>
            <select id="pay-status" v-model="statusFilter">
              <option value="">All statuses</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
          <div class="st-field">
            <label for="pay-start">From</label>
            <input id="pay-start" v-model="startDate" type="date" />
          </div>
          <div class="st-field">
            <label for="pay-end">To</label>
            <input id="pay-end" v-model="endDate" type="date" />
          </div>
          <button v-if="statusFilter || startDate || endDate" type="button" class="st-btn ghost sm clear" @click="clearFilters">Clear filters</button>
        </div>

        <div v-if="message" class="st-alert">{{ message }}</div>

        <div v-if="loading" class="state"><div class="st-loader"></div><p>Loading payment history…</p></div>

        <div v-else-if="!payments.length && !message" class="st-empty">
          <v-icon size="44">mdi-cash-multiple</v-icon>
          <h3>No payments yet</h3>
          <p>When a town or paper you closed is billed and your payout is issued, it will show up here.</p>
        </div>

        <div v-else-if="!filteredPayments.length && payments.length" class="st-empty">
          <v-icon size="40">mdi-filter-remove-outline</v-icon>
          <h3>Nothing in this range</h3>
          <p>No payments match these filters.</p>
        </div>

        <div v-else-if="filteredPayments.length" class="st-table">
          <div class="st-tr st-th row">
            <span>Amount</span><span>For</span><span>Status</span><span>Date</span><span class="r">Invoice</span>
          </div>
          <div v-for="p in filteredPayments" :key="p.id" class="st-tr row">
            <span class="amt" data-label="Amount">{{ money(p.amount) }}</span>
            <span class="for" data-label="For">{{ forLabel(p) }}</span>
            <span data-label="Status"><span class="st-badge" :class="badge(p.status)">{{ prettyStatus(p.status) }}</span></span>
            <span class="date" data-label="Date">{{ formatDate(p.timestamp) }}</span>
            <span class="r" data-label="Invoice">
              <a v-if="p.invoiceUrl" :href="p.invoiceUrl" target="_blank" rel="noopener" class="st-icon-btn" title="View invoice"><v-icon size="18">mdi-file-document-outline</v-icon></a>
              <span v-else class="st-muted">—</span>
            </span>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import StaffHeader from '@/components/StaffHeader.vue'
import { auth, db } from '@/firebase'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import '@/assets/na-staff.css'

export default {
  name: 'StaffPayments',
  components: { StaffHeader },
  data() {
    return {
      payments: [],
      loading: true,
      message: '',
      statusFilter: '',
      startDate: null,
      endDate: null,
      unsub: null,
      thisYear: new Date().getFullYear(),
    }
  },
  computed: {
    paidOnly() { return this.payments.filter(p => (p.status || '').toLowerCase() === 'paid') },
    totalPaid() { return this.paidOnly.reduce((s, p) => s + (Number(p.amount) || 0), 0) },
    paidThisYear() {
      const start = new Date(this.thisYear, 0, 1).getTime()
      return this.paidOnly.filter(p => this.tsMs(p.timestamp) >= start).reduce((s, p) => s + (Number(p.amount) || 0), 0)
    },
    outstanding() { return this.payments.filter(p => !['paid', 'refunded'].includes((p.status || '').toLowerCase())) },
    totalOutstanding() { return this.outstanding.reduce((s, p) => s + (Number(p.amount) || 0), 0) },
    paidCount() { return this.paidOnly.length },
    outstandingCount() { return this.outstanding.length },
    lastPaymentDate() {
      const arr = this.paidOnly.slice().sort((a, b) => this.tsMs(b.timestamp) - this.tsMs(a.timestamp))
      return arr[0] ? arr[0].timestamp : null
    },
    lastAmounts() {
      const arr = this.payments.slice().sort((a, b) => this.tsMs(a.timestamp) - this.tsMs(b.timestamp))
      const vals = arr.slice(-12).map(p => Number(p.amount) || 0)
      return vals.length ? vals : [0]
    },
    sparklinePoints() {
      const vals = this.lastAmounts
      if (vals.length < 2) return '0,38 200,38'
      const max = Math.max(...vals, 1)
      return vals.map((v, i) => `${(i / (vals.length - 1)) * 200},${38 - (v / max) * 32}`).join(' ')
    },
    filteredPayments() {
      const s = (this.statusFilter || '').toLowerCase()
      const start = this.startDate ? new Date(this.startDate + 'T00:00:00') : null
      const end = this.endDate ? new Date(this.endDate + 'T00:00:00') : null
      if (end) end.setHours(23, 59, 59, 999)
      return this.payments
        .filter(p => {
          const t = this.tsMs(p.timestamp)
          return (!s || (p.status || '').toLowerCase() === s) && (!start || t >= start.getTime()) && (!end || t <= end.getTime())
        })
        .sort((a, b) => this.tsMs(b.timestamp) - this.tsMs(a.timestamp))
    },
  },
  mounted() {
    const user = auth.currentUser
    if (!user) { this.$router.push('/'); return }
    this.unsub = onSnapshot(
      query(collection(db, 'staffPayments'), where('staffUid', '==', user.uid)),
      qs => {
        this.payments = qs.docs.map(d => ({ id: d.id, ...d.data() }))
        this.message = ''
        this.loading = false
      },
      e => {
        console.error('Error fetching payment history:', e)
        this.message = 'Could not load your payment history. Refresh the page or try again later.'
        this.loading = false
      },
    )
  },
  beforeDestroy() { if (this.unsub) this.unsub() },
  methods: {
    money(n) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0)) },
    tsMs(ts) {
      if (!ts) return 0
      if (ts.seconds) return ts.seconds * 1000
      if (ts.toDate) return ts.toDate().getTime()
      return new Date(ts).getTime()
    },
    formatDay(ts) {
      const ms = this.tsMs(ts)
      return ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
    },
    formatDate(ts) {
      const ms = this.tsMs(ts)
      if (!ms) return ''
      const d = new Date(ms)
      return `${this.formatDay(ts)} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    },
    // Shows which customer the payout came from, if the payment record says so.
    forLabel(p) { return p.customerName || p.townName || p.companyName || p.description || p.note || '—' },
    prettyStatus(s = '') { s = s || 'unknown'; return s.charAt(0).toUpperCase() + s.slice(1) },
    badge(s) { return { paid: 'green', pending: 'brass', failed: 'red', refunded: 'navy' }[(s || '').toLowerCase()] || '' },
    clearFilters() { this.statusFilter = ''; this.startDate = null; this.endDate = null },
    exportCSV() {
      const rows = [
        ['Amount', 'For', 'Status', 'Date', 'Invoice URL'],
        ...this.filteredPayments.map(p => [
          (Number(p.amount) || 0).toFixed(2),
          this.forLabel(p) === '—' ? '' : this.forLabel(p),
          this.prettyStatus(p.status || ''),
          this.formatDate(p.timestamp),
          p.invoiceUrl || '',
        ]),
      ]
      const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `newsappify_payments_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    goBack() { this.$router.push('/staff-dashboard') },
  },
}
</script>

<style scoped>
.kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 18px; margin-bottom: 24px; }
.kpi { display: flex; flex-direction: column; gap: 6px; border-top: 3px solid var(--na-navy); }
.kpi.owed { border-top-color: var(--na-brass); }
.k-label { font-size: 0.74rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--na-ink-2); }
.k-value { font-family: var(--na-serif); font-weight: 700; font-size: 2.1rem; line-height: 1.05; color: var(--na-ink); font-variant-numeric: tabular-nums; }
.k-value.small { font-size: 1.35rem; }
.k-meta { font-size: 0.87rem; color: var(--na-ink-2); }
.spark { width: 100%; height: 36px; }

.filters { display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap; margin-bottom: 18px; }
.filters .st-field { min-width: 170px; }
.clear { margin-bottom: 2px; }

.state { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 48px 20px; color: var(--na-ink-2); }

.row { grid-template-columns: 1fr 1.6fr 0.9fr 1.3fr 70px; }
.r { text-align: right; justify-self: end; }
.amt { font-weight: 700; font-variant-numeric: tabular-nums; color: var(--na-ink); }
.for { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.date { color: var(--na-ink-2); font-size: 0.93rem; }

@media (max-width: 1000px) {
  .kpis { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 720px) {
  .kpis { grid-template-columns: 1fr; }
  .filters .st-field { flex: 1 1 100%; }
  .st-th { display: none; }
  .row { grid-template-columns: 1fr 1fr; row-gap: 10px; }
  .row > span::before { content: attr(data-label); display: block; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--na-ink-2); margin-bottom: 2px; }
  .r { text-align: left; justify-self: start; }
}
</style>